/**
 * Email domain typo detection and suggestion utility.
 *
 * Detects common misspellings in popular email domains (e.g. gmail.comt,
 * hotmal.com) and suggests the correct domain. Uses Levenshtein distance
 * for fuzzy matching against a curated list of providers popular in Brazil.
 */

// Order matters: more common/global domains come first so that when two
// domains tie at the same Levenshtein distance, the more popular one wins.
const KNOWN_DOMAINS: readonly string[] = [
  // Global (most common first)
  'gmail.com',
  'hotmail.com',
  'outlook.com',
  'yahoo.com',
  'yahoo.com.br',
  'icloud.com',
  'live.com',
  'protonmail.com',
  'proton.me',
  'zoho.com',
  // Brazil-specific
  'bol.com.br',
  'uol.com.br',
  'terra.com.br',
  'globo.com',
  'globomail.com',
  'zipmail.com.br',
  'hotmail.com.br',
  'outlook.com.br',
  'live.com.br',
] as const;

/**
 * Real providers that must be accepted as-is but must NEVER be used as fuzzy
 * correction targets.
 *
 * All three sit one edit from `gmail.com`, so treating them as typos would
 * nag their genuine users on every signup.
 *
 * They are deliberately NOT in {@link KNOWN_DOMAINS}, because that list is also
 * the candidate set for fuzzy matching: adding `mail.com` there made every real
 * domain one edit away from *it* a suggestion target — `mall.com`, `sail.com`,
 * `tail.com`, `nail.com`, `rail.com` are all one substitution from `mail.com`.
 * While a name-distance rule still gated signup, that mistake refused real
 * businesses outright. Accept-only, never a target.
 */
const EXACT_VALID_DOMAINS: readonly string[] = [
  'ymail.com',
  'email.com',
  'mail.com',
] as const;

/** Whether a domain is spelled correctly — either a fuzzy target or accept-only. */
function isKnownGoodDomain(domain: string): boolean {
  return KNOWN_DOMAINS.includes(domain) || EXACT_VALID_DOMAINS.includes(domain);
}

/** Maximum edit distance to consider a domain a likely typo. */
const MAX_DISTANCE = 2;

/**
 * Compute the Levenshtein edit distance between two strings.
 *
 * Uses the classic dynamic-programming approach with O(min(a,b)) space.
 */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  // Keep the shorter string in `b` so the column array is smaller.
  if (a.length < b.length) {
    [a, b] = [b, a];
  }

  const bLen = b.length;
  const prev = Array.from({ length: bLen + 1 }, (_, i) => i);

  for (let i = 1; i <= a.length; i++) {
    let prevDiag = prev[0];
    prev[0] = i;

    for (let j = 1; j <= bLen; j++) {
      const temp = prev[j];
      if (a[i - 1] === b[j - 1]) {
        prev[j] = prevDiag;
      } else {
        prev[j] = 1 + Math.min(prevDiag, prev[j], prev[j - 1]);
      }
      prevDiag = temp;
    }
  }

  return prev[bLen];
}

/**
 * Extract the domain portion from an email address.
 *
 * @returns The lowercased domain, or `null` if the email is malformed.
 */
function extractDomain(email: string): {
  localPart: string;
  domain: string;
} | null {
  if (!email || !email.includes('@')) return null;

  const parts = email.split('@');
  if (parts.length !== 2) return null;

  const [localPart, domain] = parts;
  if (!localPart || !domain) return null;

  return { localPart, domain: domain.toLowerCase() };
}

/**
 * Find the closest known domain to the given domain.
 *
 * Scans the full `KNOWN_DOMAINS` list and returns the best match within
 * `MAX_DISTANCE`. Short-circuits once the best distance is 1 (cannot
 * improve further).
 */
function findClosestDomain(
  domain: string,
): { knownDomain: string; distance: number } | null {
  let bestDomain: string | null = null;
  let bestDistance = MAX_DISTANCE + 1;

  for (const knownDomain of KNOWN_DOMAINS) {
    // If we already have a distance-1 match, nothing can beat it.
    if (bestDistance === 1) break;

    // `||a| - |b||` is a lower bound on the edit distance, so a candidate whose
    // length differs by more than MAX_DISTANCE cannot possibly match. Skipping
    // it is exact (no behaviour change) and bounds the work: these validators
    // run on unauthenticated signup input *ahead* of the rate limiter, so an
    // oversized-but-syntactically-valid address would otherwise force a full
    // O(len x 14) pass per candidate on every request.
    if (Math.abs(domain.length - knownDomain.length) > MAX_DISTANCE) {
      continue;
    }

    const distance = levenshtein(domain, knownDomain);

    if (distance < bestDistance) {
      bestDistance = distance;
      bestDomain = knownDomain;
    }
  }

  if (bestDomain && bestDistance <= MAX_DISTANCE) {
    return { knownDomain: bestDomain, distance: bestDistance };
  }

  return null;
}

/**
 * Detect false `.br` suffix: domains like `gmail.com.br` where the base
 * domain (`gmail.com`) exists in the known list but the `.br` variant does not.
 *
 * @returns The correct domain without `.br`, or `null` if not a false suffix.
 */
function checkFalseBrSuffix(domain: string): string | null {
  if (!domain.endsWith('.br')) return null;

  // The .br version itself is a correct domain — not a false suffix.
  if (isKnownGoodDomain(domain)) return null;

  // Remove the `.br` suffix to get the base domain
  const baseDomain = domain.slice(0, -3);
  if (!baseDomain) return null;

  // Exact match: base is correct (e.g. gmail.com.br → gmail.com)
  if (isKnownGoodDomain(baseDomain)) {
    return baseDomain;
  }

  // Fuzzy match: base is a typo of a known domain
  // (e.g. iclod.com.br → iclod.com → distance 1 from icloud.com)
  const match = findClosestDomain(baseDomain);
  if (match && match.distance <= 1) {
    return match.knownDomain;
  }

  return null;
}

/**
 * Suffixes that are confirmed not to exist, observed in the account base.
 *
 * A closed list, deliberately. Two earlier attempts at this rule inferred
 * "not a real suffix" from a curated allowlist plus an edit-distance test, and
 * both leaked false positives because the allowlist can never be proven
 * complete:
 *
 *  - the first version matched on the domain *name*, which refused real
 *    businesses — `terra.com.br` / `tetra.com.br` / `serra.com.br` are one edit
 *    apart and all plausible, 17 of 32 surveyed neighbours were rejected;
 *  - the second matched unrecognised suffixes within one edit of a common one,
 *    which refused `.bio` (one edit from `biz`), `.ong` (one from `org`, widely
 *    used by Brazilian NGOs) and `.bom` (one from `com`, a real gTLD operated
 *    by NIC.br) — all genuine IANA entries missing from the allowlist.
 *
 * Inverting it removes the class outright. Membership is the whole test, so an
 * address can only be refused if its suffix is literally listed here. A missing
 * entry costs a missed detection, which falls through to the dismissible
 * suggestion tier — never a lockout. That asymmetry is the point: a miss is
 * recoverable, a false refusal means a real customer cannot open an account.
 *
 * Every entry is a suffix seen on real signups in the PIERRE-4012 analysis
 * (counts are users affected). Extend it from that query rather than by
 * guessing — the `email_domain_typo` analytics bucket shows when refusals move.
 */
const CONFIRMED_INVALID_SUFFIXES: ReadonlySet<string> = new Set([
  'comm', // 697
  'con', //  642
  'coml', // 611
  'coma', // 537
  'comr', // 368
  'comj', // 358
]);

/**
 * Whether an email address ends in a suffix confirmed not to exist.
 *
 * This is the server-side signup block, and it is deliberately far narrower
 * than {@link checkEmailDomain}'s fuzzy matching. It refuses only what has been
 * measured, never what has been inferred, so it cannot turn away a legitimate
 * domain no matter how unusual.
 *
 * A mistyped domain *name* is never refused here — that stays a dismissible
 * suggestion on both clients, which is where an ambiguous signal belongs.
 */
export function hasInvalidSuffix(email: string): boolean {
  const parsed = extractDomain(email.trim());
  if (!parsed) return false;

  const labels = parsed.domain.split('.');
  const suffix = labels[labels.length - 1];

  return Boolean(suffix) && CONFIRMED_INVALID_SUFFIXES.has(suffix);
}

/**
 * Message used by every server-side signup schema that rejects an address
 * whose suffix does not exist (see {@link hasInvalidSuffix}).
 *
 * It is deliberately both user-facing Portuguese copy *and* the marker the
 * signup paths match on to turn a generic `invalid_data` rejection into a
 * field-specific "fix your email domain" response. Matching on this exact
 * string is what lets `register()` answer `invalid_email_domain` instead of a
 * dead-end `Dados inválidos` at the end of onboarding, so don't reword it
 * without updating the call sites that compare against it.
 */
export const EMAIL_DOMAIN_TYPO_MESSAGE =
  'Verifique o final do e-mail: esse domínio não existe.';

export interface EmailDomainSuggestion {
  /** The original email address as entered by the user. */
  original: string;
  /** The suggested corrected email address, or `null` if no typo detected. */
  suggested: string | null;
  /** The suggested correct domain, or `null` if no typo detected. */
  suggestedDomain: string | null;
  /** Whether a potential typo was detected. */
  hasTypo: boolean;
}

/**
 * Check an email address for common domain typos.
 *
 * @param email - The email address to check.
 * @returns An `EmailDomainSuggestion` with a corrected suggestion when a
 *   likely typo is detected, or `hasTypo: false` when the domain looks fine.
 *
 * @example
 * ```ts
 * checkEmailDomain('user@gmail.comt');
 * // → { hasTypo: true, suggested: 'user@gmail.com', suggestedDomain: 'gmail.com', original: 'user@gmail.comt' }
 *
 * checkEmailDomain('user@gmail.com');
 * // → { hasTypo: false, suggested: null, suggestedDomain: null, original: 'user@gmail.com' }
 * ```
 */
export function checkEmailDomain(rawEmail: string): EmailDomainSuggestion {
  const email = rawEmail.trim();
  const noSuggestion: EmailDomainSuggestion = {
    original: email,
    suggested: null,
    suggestedDomain: null,
    hasTypo: false,
  };

  const parsed = extractDomain(email);
  if (!parsed) return noSuggestion;

  const { localPart, domain } = parsed;

  // Exact match — no typo.
  if (isKnownGoodDomain(domain)) {
    return noSuggestion;
  }

  // Detect false `.br` suffix: e.g. `gmail.com.br` → `gmail.com`.
  // If the domain ends with `.br` and removing it yields a known domain
  // (but the `.br` version is NOT known), it's a typo.
  const withoutBr = checkFalseBrSuffix(domain);
  if (withoutBr) {
    return {
      original: email,
      suggested: `${localPart}@${withoutBr}`,
      suggestedDomain: withoutBr,
      hasTypo: true,
    };
  }

  const match = findClosestDomain(domain);

  if (match) {
    return {
      original: email,
      suggested: `${localPart}@${match.knownDomain}`,
      suggestedDomain: match.knownDomain,
      hasTypo: true,
    };
  }

  return noSuggestion;
}
